/**
 * Rules Engine V2 — Types & Constants
 *
 * General-purpose automation builder: admin selects a FHIR resource type,
 * a trigger operation (created / updated / manual-run), field filters, branch
 * conditions, and actions (send-notification, send-email, assign-template-forms,
 * set-claim-field for domain claim-assembly).
 *
 * Storage model:
 *   - Each rule is a `PlanDefinition` (canonical URL per stable rule key).
 *   - Branches are `PlanDefinition.action[]` entries with extension-backed
 *     structured condition trees and action definitions.
 *   - Trigger metadata lives in extensions on the PlanDefinition.
 *   - Actions carry execution-ready metadata (recipient policies, dedupe)
 *     as extensions so a Bot/executor can create notifications or assign
 *     forms idempotently.
 *   - Published versions are immutable; editing creates a new draft.
 *
 * Migration: V1 lifecycle events (client-created, client-intake-completed, etc.)
 * are converted to generic resource+operation+filters on read. New rules save
 * in the V2 generic schema. Old PlanDefinitions continue to parse via migration.
 */
import type { PlanDefinition } from '@medplum/fhirtypes';
import { CMS_PLACE_OF_SERVICE_CODE_SYSTEM, CPT_CODE_SYSTEM } from '@unify/fhir-model/constants';

// ---------------------------------------------------------------------------
// Extension URLs
// ---------------------------------------------------------------------------

const RE_EXT_BASE = 'https://unify.health/fhir/StructureDefinition/rules-engine';

export const RE_DOMAIN_EXTENSION = `${RE_EXT_BASE}-domain`;
export const RE_SCOPE_EXTENSION = `${RE_EXT_BASE}-scope`;
export const RE_TRIGGER_EXTENSION = `${RE_EXT_BASE}-trigger`;
export const RE_ROLLOUT_EXTENSION = `${RE_EXT_BASE}-rollout`;
export const RE_RUNG_CONDITION_EXTENSION = `${RE_EXT_BASE}-condition`;
/** Persisted on PlanDefinition actions that represent the workflow Else lane. */
export const RE_RUNG_ELSE_BRANCH_EXTENSION = `${RE_RUNG_CONDITION_EXTENSION}-else-branch`;
export const RE_RUNG_ACTION_EXTENSION = `${RE_EXT_BASE}-action`;
export const RE_CONFLICT_POLICY_EXTENSION = `${RE_EXT_BASE}-conflict-policy`;
export const RE_EVALUATION_KEY_EXTENSION = `${RE_EXT_BASE}-evaluation-key`;
export const RE_STABLE_RULE_KEY_EXTENSION = `${RE_EXT_BASE}-stable-rule-key`;
export const RE_V1_MARKER_EXTENSION = `${RE_EXT_BASE}-v1`;
export const RE_WORKFLOW_STEPS_EXTENSION = `${RE_EXT_BASE}-workflow-steps`;
export const RE_SCHEMA_VERSION_EXTENSION = `${RE_EXT_BASE}-schema-version`;

export const RE_CANONICAL_URL_PREFIX =
  'https://unify.health/fhir/PlanDefinition/rules-engine/';

/** Schema version: 1 = legacy lifecycle, 2 = generic resource trigger. */
export type RulesEngineSchemaVersion = 1 | 2;
export const CURRENT_SCHEMA_VERSION: RulesEngineSchemaVersion = 2;

// ---------------------------------------------------------------------------
// Enums & domain constants
// ---------------------------------------------------------------------------

export type RulePackStatus = 'draft' | 'active' | 'retired';

export const RULE_PACK_STATUS_LABELS: Record<RulePackStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  retired: 'Retired',
};

export const RULE_PACK_STATUS_COLORS: Record<RulePackStatus, string> = {
  draft: 'yellow',
  active: 'green',
  retired: 'gray',
};

export type RulePackDomain = 'client-lifecycle' | 'client-intake' | 'client-events' | 'billing' | 'claim-assembly';

export const RULE_PACK_DOMAINS: { value: RulePackDomain; label: string }[] = [
  { value: 'client-intake', label: 'Client Intake' },
  { value: 'client-events', label: 'Client Events' },
  { value: 'billing', label: 'Billing' },
  { value: 'claim-assembly', label: 'Claim Assembly' },
];

export function isClaimAssemblyDomain(domain: RulePackDomain): boolean {
  return domain === 'claim-assembly';
}

// ---------------------------------------------------------------------------
// Generic trigger operations (replaces lifecycle event kinds)
// ---------------------------------------------------------------------------

export type TriggerOperation = 'created' | 'updated' | 'manual-run';

export const TRIGGER_OPERATIONS: { value: TriggerOperation; label: string; description: string }[] = [
  { value: 'created', label: 'Created', description: 'When a new resource is created' },
  { value: 'updated', label: 'Updated', description: 'When an existing resource is updated' },
  { value: 'manual-run', label: 'Manual run', description: 'Triggered manually via Task' },
];

// ---------------------------------------------------------------------------
// Resource Type Registry — triggerable FHIR objects
// ---------------------------------------------------------------------------

export type TriggerResourceType =
  | 'Patient'
  | 'QuestionnaireResponse'
  | 'Questionnaire'
  | 'ServiceRequest'
  | 'EpisodeOfCare'
  | 'Task'
  | 'Communication'
  | 'CommunicationRequest'
  | 'CareTeam'
  | 'Practitioner'
  | 'PractitionerRole'
  | 'RelatedPerson'
  | 'Organization'
  | 'Location'
  | 'Appointment'
  | 'Encounter'
  | 'Observation'
  | 'DocumentReference'
  | 'Consent'
  | 'Coverage'
  | 'ChargeItem'
  | 'Claim'
  | 'ExplanationOfBenefit'
  | 'ActivityDefinition'
  | 'PlanDefinition';

export type ResourceCategory = 'care-lifecycle' | 'scheduling' | 'clinical' | 'financial' | 'notification' | 'admin';

export interface ResourceTypeRegistryEntry {
  resourceType: TriggerResourceType;
  label: string;
  description: string;
  category: ResourceCategory;
  supportedOperations: TriggerOperation[];
  subscriptionSupported: boolean;
  /** Whether this resource is shown by default in the resource type selector. */
  defaultVisible: boolean;
}

export const RESOURCE_TYPE_REGISTRY: ResourceTypeRegistryEntry[] = [
  // Care lifecycle
  { resourceType: 'Patient', label: 'Patient', description: 'Client / patient record', category: 'care-lifecycle', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: true },
  { resourceType: 'QuestionnaireResponse', label: 'Questionnaire Response', description: 'Completed forms and assessments', category: 'care-lifecycle', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: true },
  { resourceType: 'Questionnaire', label: 'Questionnaire', description: 'Form/template definition', category: 'care-lifecycle', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: true },
  { resourceType: 'ServiceRequest', label: 'Service Request', description: 'Episode of Care requests and referrals', category: 'care-lifecycle', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: true },
  { resourceType: 'EpisodeOfCare', label: 'Episode of Care', description: 'Care episodes and treatment phases', category: 'care-lifecycle', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: true },
  { resourceType: 'Task', label: 'Task', description: 'Work items, form assignments, email tasks', category: 'care-lifecycle', supportedOperations: ['created', 'updated', 'manual-run'], subscriptionSupported: true, defaultVisible: true },
  { resourceType: 'CareTeam', label: 'Care Team', description: 'Provider care teams', category: 'care-lifecycle', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },
  { resourceType: 'Consent', label: 'Consent', description: 'Patient consents and authorizations', category: 'care-lifecycle', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },

  // Notification
  { resourceType: 'Communication', label: 'Communication', description: 'In-app and email delivery records', category: 'notification', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: true },
  { resourceType: 'CommunicationRequest', label: 'Communication Request', description: 'Delivery requests', category: 'notification', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },
  { resourceType: 'ActivityDefinition', label: 'Activity Definition', description: 'Email notification templates', category: 'notification', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: true },

  // Scheduling
  { resourceType: 'Appointment', label: 'Appointment', description: 'Scheduled sessions and meetings', category: 'scheduling', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },
  { resourceType: 'Encounter', label: 'Encounter', description: 'Clinical encounters', category: 'scheduling', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },

  // Clinical
  { resourceType: 'Observation', label: 'Observation', description: 'Clinical observations and measurements', category: 'clinical', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },
  { resourceType: 'DocumentReference', label: 'Document Reference', description: 'Attached documents and files', category: 'clinical', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },

  // Financial
  { resourceType: 'Coverage', label: 'Coverage', description: 'Insurance coverage', category: 'financial', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },
  { resourceType: 'Claim', label: 'Claim', description: 'Billing claims', category: 'financial', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },
  { resourceType: 'ExplanationOfBenefit', label: 'Explanation of Benefit', description: 'Insurance benefit explanations', category: 'financial', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },

  // Admin
  { resourceType: 'Practitioner', label: 'Practitioner', description: 'Provider profiles', category: 'admin', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },
  { resourceType: 'PractitionerRole', label: 'Practitioner Role', description: 'Provider role assignments', category: 'admin', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },
  { resourceType: 'RelatedPerson', label: 'Related Person', description: 'Guardians, emergency contacts', category: 'admin', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },
  { resourceType: 'Organization', label: 'Organization', description: 'Practice organizations', category: 'admin', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },
  { resourceType: 'Location', label: 'Location', description: 'Practice locations', category: 'admin', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },
  { resourceType: 'PlanDefinition', label: 'Plan Definition', description: 'Rules Engine rules and care plans', category: 'admin', supportedOperations: ['created', 'updated'], subscriptionSupported: true, defaultVisible: false },
];

export const RESOURCE_CATEGORY_LABELS: Record<ResourceCategory, string> = {
  'care-lifecycle': 'Care Lifecycle',
  scheduling: 'Scheduling',
  clinical: 'Clinical',
  financial: 'Financial',
  notification: 'Notifications',
  admin: 'Administration',
};

/** Flat select data for backwards compatibility and simple UI. */
export const TRIGGER_RESOURCE_TYPES: { value: TriggerResourceType; label: string }[] =
  RESOURCE_TYPE_REGISTRY.map((r) => ({ value: r.resourceType, label: r.label }));

/** Default-visible resources for the primary selector. */
export const DEFAULT_VISIBLE_RESOURCE_TYPES = RESOURCE_TYPE_REGISTRY.filter((r) => r.defaultVisible);

export function getResourceRegistryEntry(rt: TriggerResourceType): ResourceTypeRegistryEntry | undefined {
  return RESOURCE_TYPE_REGISTRY.find((r) => r.resourceType === rt);
}

// ---------------------------------------------------------------------------
// Field Registry — filterable/condition fields per resource type
// ---------------------------------------------------------------------------

export type FieldValueType = 'string' | 'boolean' | 'number' | 'date' | 'dateTime' | 'code' | 'coding' | 'reference' | 'canonical' | 'enum';

export interface ResourceFieldDefinition {
  resourceType: TriggerResourceType;
  fieldPath: string;
  label: string;
  description: string;
  valueType: FieldValueType;
  operators: ConditionOperator[];
  enumOptions?: { value: string; label: string }[];
  /** Whether this field can be used in trigger resource filters. */
  usableInTriggerFilter: boolean;
  /** Whether this field can be used in branch conditions. */
  usableInCondition: boolean;
  /** Group label for UI display (e.g. "Patient", "QuestionnaireResponse"). */
  group: string;
}

/**
 * Central field registry. Powers trigger filter dropdowns, branch condition
 * dropdowns, runtime extraction, and validation.
 */
export const RESOURCE_FIELD_REGISTRY: ResourceFieldDefinition[] = [
  // Patient fields
  { resourceType: 'Patient', fieldPath: 'patient.active', label: 'Active', description: 'Whether the patient record is active', valueType: 'boolean', operators: ['equals', 'not-equals', 'exists', 'not-exists'], enumOptions: [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }], usableInTriggerFilter: false, usableInCondition: true, group: 'Patient' },
  { resourceType: 'Patient', fieldPath: 'patient.email.exists', label: 'Email exists', description: 'Whether the patient has an email on file', valueType: 'boolean', operators: ['equals', 'not-equals', 'exists', 'not-exists'], enumOptions: [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }], usableInTriggerFilter: false, usableInCondition: true, group: 'Patient' },
  { resourceType: 'Patient', fieldPath: 'patient.communicationPreference.emailAllowed', label: 'Email allowed', description: 'Whether email communication is allowed', valueType: 'boolean', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }], usableInTriggerFilter: false, usableInCondition: true, group: 'Patient' },
  { resourceType: 'Patient', fieldPath: 'patient.age', label: 'Age', description: 'Patient age computed from birthDate', valueType: 'number', operators: ['equals', 'not-equals', 'greater-than', 'less-than', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Patient' },
  { resourceType: 'Patient', fieldPath: 'patient.address.state', label: 'State', description: 'Patient address state', valueType: 'enum', operators: ['equals', 'not-equals', 'in-list', 'not-in-list', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Patient' },

  // QuestionnaireResponse fields
  { resourceType: 'QuestionnaireResponse', fieldPath: 'status', label: 'Status', description: 'QR status (completed, in-progress, etc.)', valueType: 'code', operators: ['equals', 'not-equals', 'in-list', 'not-in-list'], enumOptions: [{ value: 'completed', label: 'Completed' }, { value: 'in-progress', label: 'In progress' }, { value: 'draft', label: 'Draft' }, { value: 'amended', label: 'Amended' }, { value: 'entered-in-error', label: 'Entered in error' }, { value: 'stopped', label: 'Stopped' }], usableInTriggerFilter: true, usableInCondition: false, group: 'Questionnaire Response' },
  { resourceType: 'QuestionnaireResponse', fieldPath: 'questionnaire', label: 'Questionnaire', description: 'Reference to the Questionnaire template', valueType: 'canonical', operators: ['equals', 'not-equals'], usableInTriggerFilter: true, usableInCondition: false, group: 'Questionnaire Response' },
  { resourceType: 'QuestionnaireResponse', fieldPath: 'intake.status', label: 'Status', description: 'QR status (completed, in-progress, etc.)', valueType: 'code', operators: ['equals', 'not-equals', 'in-list', 'not-in-list'], enumOptions: [{ value: 'completed', label: 'Completed' }, { value: 'in-progress', label: 'In progress' }, { value: 'draft', label: 'Draft' }, { value: 'amended', label: 'Amended' }, { value: 'entered-in-error', label: 'Entered in error' }, { value: 'stopped', label: 'Stopped' }], usableInTriggerFilter: false, usableInCondition: true, group: 'Questionnaire Response' },
  { resourceType: 'QuestionnaireResponse', fieldPath: 'intake.submittedAt', label: 'Submitted at', description: 'When the response was submitted', valueType: 'dateTime', operators: ['exists', 'not-exists', 'past-hours'], usableInTriggerFilter: false, usableInCondition: true, group: 'Questionnaire Response' },
  { resourceType: 'QuestionnaireResponse', fieldPath: 'intake.completedAt', label: 'Completed at', description: 'When the response was completed', valueType: 'dateTime', operators: ['exists', 'not-exists', 'past-hours'], usableInTriggerFilter: false, usableInCondition: true, group: 'Questionnaire Response' },

  // ServiceRequest fields
  { resourceType: 'ServiceRequest', fieldPath: 'serviceRequest.status', label: 'Status', description: 'SR status', valueType: 'code', operators: ['equals', 'not-equals', 'in-list', 'not-in-list'], enumOptions: [{ value: 'draft', label: 'Draft' }, { value: 'active', label: 'Active' }, { value: 'on-hold', label: 'On hold' }, { value: 'revoked', label: 'Revoked' }, { value: 'completed', label: 'Completed' }, { value: 'entered-in-error', label: 'Entered in error' }, { value: 'unknown', label: 'Unknown' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Service Request' },
  { resourceType: 'ServiceRequest', fieldPath: 'code.coding', label: 'Code', description: 'ServiceRequest code (e.g. new-episode-of-care)', valueType: 'coding', operators: ['equals', 'not-equals', 'in-list'], usableInTriggerFilter: true, usableInCondition: false, group: 'Service Request' },
  { resourceType: 'ServiceRequest', fieldPath: 'category.coding', label: 'Category', description: 'ServiceRequest category', valueType: 'coding', operators: ['equals', 'not-equals', 'in-list'], usableInTriggerFilter: true, usableInCondition: false, group: 'Service Request' },

  // EpisodeOfCare fields
  { resourceType: 'EpisodeOfCare', fieldPath: 'episode.status', label: 'Status', description: 'Episode status', valueType: 'code', operators: ['equals', 'not-equals', 'in-list', 'not-in-list'], enumOptions: [{ value: 'planned', label: 'Planned' }, { value: 'waitlist', label: 'Waitlist' }, { value: 'active', label: 'Active' }, { value: 'onhold', label: 'On hold' }, { value: 'finished', label: 'Finished' }, { value: 'cancelled', label: 'Cancelled' }, { value: 'entered-in-error', label: 'Entered in error' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Episode of Care' },
  { resourceType: 'EpisodeOfCare', fieldPath: 'episode.type', label: 'Type', description: 'Therapy service type (individual, couples, family, etc.)', valueType: 'coding', operators: ['equals', 'not-equals', 'in-list', 'not-in-list'], enumOptions: [{ value: 'individual', label: 'Individual Therapy' }, { value: 'couples', label: 'Couples Therapy' }, { value: 'family', label: 'Family Therapy' }, { value: 'group', label: 'Group Therapy' }, { value: 'iop', label: 'Intensive Outpatient Program' }, { value: 'medication-management', label: 'Medication Management' }, { value: 'psych-testing', label: 'Psychological Testing' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Episode of Care' },
  { resourceType: 'EpisodeOfCare', fieldPath: 'episode.startedAt', label: 'Started at', description: 'When the episode started', valueType: 'dateTime', operators: ['exists', 'not-exists', 'past-hours'], usableInTriggerFilter: false, usableInCondition: true, group: 'Episode of Care' },

  // Task fields
  { resourceType: 'Task', fieldPath: 'task.status', label: 'Status', description: 'Task status', valueType: 'code', operators: ['equals', 'not-equals', 'in-list', 'not-in-list'], enumOptions: [{ value: 'requested', label: 'Requested' }, { value: 'accepted', label: 'Accepted' }, { value: 'in-progress', label: 'In progress' }, { value: 'completed', label: 'Completed' }, { value: 'cancelled', label: 'Cancelled' }, { value: 'rejected', label: 'Rejected' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Task' },
  { resourceType: 'Task', fieldPath: 'task.code', label: 'Code', description: 'Task type code (manual-rule-run, send-email-notification, etc.)', valueType: 'coding', operators: ['equals', 'not-equals', 'in-list'], enumOptions: [{ value: 'manual-rule-run', label: 'Manual rule run' }, { value: 'send-email-notification', label: 'Send email notification' }, { value: 'client-form-assignment', label: 'Client form assignment' }, { value: 'rules-engine-form-assignment', label: 'Rules Engine form assignment' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Task' },
  { resourceType: 'Task', fieldPath: 'task.linkedAppointment', label: 'Linked appointment', description: 'Task references an Appointment via basedOn, reasonReference, input, or focus', valueType: 'boolean', operators: ['exists', 'not-exists'], usableInTriggerFilter: true, usableInCondition: true, group: 'Task' },

  // Communication fields
  { resourceType: 'Communication', fieldPath: 'communication.status', label: 'Status', description: 'Communication status', valueType: 'code', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'in-progress', label: 'In progress (unread)' }, { value: 'completed', label: 'Completed (read)' }, { value: 'not-done', label: 'Not done' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Communication' },
  { resourceType: 'Communication', fieldPath: 'communication.medium', label: 'Medium', description: 'Communication medium (in-app, email)', valueType: 'coding', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'in-app', label: 'In-app' }, { value: 'EMAILWRIT', label: 'Email' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Communication' },
  { resourceType: 'Communication', fieldPath: 'communication.category', label: 'Category', description: 'Communication category', valueType: 'coding', operators: ['equals', 'not-equals', 'in-list'], usableInTriggerFilter: true, usableInCondition: true, group: 'Communication' },

  // ActivityDefinition fields
  { resourceType: 'ActivityDefinition', fieldPath: 'activityDefinition.status', label: 'Status', description: 'Template status (draft, active, retired)', valueType: 'code', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'draft', label: 'Draft' }, { value: 'active', label: 'Active' }, { value: 'retired', label: 'Retired' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Activity Definition' },
  { resourceType: 'ActivityDefinition', fieldPath: 'activityDefinition.name', label: 'Name', description: 'Template name', valueType: 'string', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Activity Definition' },

  // Questionnaire fields
  { resourceType: 'Questionnaire', fieldPath: 'questionnaire.status', label: 'Status', description: 'Questionnaire status', valueType: 'code', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'draft', label: 'Draft' }, { value: 'active', label: 'Active' }, { value: 'retired', label: 'Retired' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Questionnaire' },
  { resourceType: 'Questionnaire', fieldPath: 'questionnaire.url', label: 'Canonical URL', description: 'Questionnaire canonical URL', valueType: 'canonical', operators: ['equals', 'not-equals'], usableInTriggerFilter: true, usableInCondition: true, group: 'Questionnaire' },

  // Appointment fields (Unify calendar model)
  { resourceType: 'Appointment', fieldPath: 'appointment.status', label: 'Status', description: 'FHIR Appointment status (proposed, booked, fulfilled, cancelled, noshow)', valueType: 'code', operators: ['equals', 'not-equals', 'in-list', 'not-in-list'], enumOptions: [{ value: 'proposed', label: 'Proposed' }, { value: 'booked', label: 'Booked' }, { value: 'fulfilled', label: 'Fulfilled' }, { value: 'cancelled', label: 'Cancelled' }, { value: 'noshow', label: 'No show' }, { value: 'pending', label: 'Pending' }, { value: 'arrived', label: 'Arrived' }, { value: 'checked-in', label: 'Checked in' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.start', label: 'Start time', description: 'Appointment start (ISO datetime)', valueType: 'dateTime', operators: ['exists', 'not-exists', 'past-hours', 'greater-than', 'less-than'], usableInTriggerFilter: true, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.end', label: 'End time', description: 'Appointment end (ISO datetime)', valueType: 'dateTime', operators: ['exists', 'not-exists', 'past-hours', 'greater-than', 'less-than'], usableInTriggerFilter: true, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.minutesDuration', label: 'Duration (minutes)', description: 'Scheduled duration in minutes', valueType: 'number', operators: ['equals', 'not-equals', 'greater-than', 'less-than', 'exists', 'not-exists'], usableInTriggerFilter: true, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.category', label: 'Calendar category', description: 'Session, group, meeting, or time entry (serviceCategory)', valueType: 'code', operators: ['equals', 'not-equals', 'in-list', 'not-in-list'], enumOptions: [{ value: 'session', label: 'Session' }, { value: 'group', label: 'Group' }, { value: 'meeting', label: 'Meeting' }, { value: 'time-entry', label: 'Time entry' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.modality', label: 'Modality', description: 'In-person, telehealth, or hybrid (appointmentType). On Claim Assembly rules this is authoring-only until overlay evaluates the Appointment graph.', valueType: 'code', operators: ['equals', 'not-equals', 'in-list'], enumOptions: [{ value: 'ROUTINE', label: 'In-person' }, { value: 'VR', label: 'Telehealth / virtual' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.serviceType', label: 'Service type code', description: 'Therapy service type or CPT code from serviceType', valueType: 'coding', operators: ['equals', 'not-equals', 'in-list', 'exists', 'not-exists'], usableInTriggerFilter: true, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.hasPatient', label: 'Has patient participant', description: 'Whether a Patient participant (SBJ) is on the appointment', valueType: 'boolean', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }], usableInTriggerFilter: false, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.hasVideoLink', label: 'Has video link', description: 'Whether a video meeting URL extension is present', valueType: 'boolean', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }], usableInTriggerFilter: false, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.recurringPattern', label: 'Recurring pattern', description: 'Recurrence code when the appointment repeats', valueType: 'code', operators: ['equals', 'not-equals', 'exists', 'not-exists'], enumOptions: [{ value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }, { value: 'biweekly', label: 'Biweekly' }, { value: 'monthly', label: 'Monthly' }], usableInTriggerFilter: false, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.patientId', label: 'Patient id', description: 'Primary patient participant id', valueType: 'string', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.practitionerId', label: 'Practitioner id', description: 'Primary practitioner participant id', valueType: 'string', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.locationId', label: 'Location id', description: 'Practice site Location participant id (LOC)', valueType: 'string', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.locationIdentifier', label: 'Location identifier', description: 'Stable business identifier of the Location (LOCATION_IDENTIFIER_SYSTEM)', valueType: 'string', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.locationName', label: 'Location name', description: 'Location.name for the appointment site', valueType: 'string', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.locationAddress', label: 'Location address', description: 'Display address for the appointment Location (override or formatted address line)', valueType: 'string', operators: ['exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.locationParkingInstructions', label: 'Location parking instructions', description: 'Client-facing parking instructions from the Location extension', valueType: 'string', operators: ['exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.locationArrivalInstructions', label: 'Location arrival instructions', description: 'Client-facing arrival instructions from the Location extension', valueType: 'string', operators: ['exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.locationWaitingRoomInstructions', label: 'Location waiting room instructions', description: 'Client-facing waiting room instructions from the Location extension', valueType: 'string', operators: ['exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Appointment' },
  { resourceType: 'Appointment', fieldPath: 'appointment.description', label: 'Description', description: 'Free-text appointment description', valueType: 'string', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Appointment' },

  // Encounter fields (linked from Appointment or direct Encounter trigger)
  { resourceType: 'Encounter', fieldPath: 'encounter.status', label: 'Status', description: 'Encounter status', valueType: 'code', operators: ['equals', 'not-equals', 'in-list', 'not-in-list'], enumOptions: [{ value: 'planned', label: 'Planned' }, { value: 'arrived', label: 'Arrived' }, { value: 'triaged', label: 'Triaged' }, { value: 'in-progress', label: 'In progress' }, { value: 'onleave', label: 'On leave' }, { value: 'finished', label: 'Finished' }, { value: 'cancelled', label: 'Cancelled' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Encounter' },
  { resourceType: 'Encounter', fieldPath: 'encounter.periodStart', label: 'Period start', description: 'Encounter period start', valueType: 'dateTime', operators: ['exists', 'not-exists', 'past-hours'], usableInTriggerFilter: true, usableInCondition: true, group: 'Encounter' },
  { resourceType: 'Encounter', fieldPath: 'encounter.periodEnd', label: 'Period end', description: 'Encounter period end', valueType: 'dateTime', operators: ['exists', 'not-exists', 'past-hours'], usableInTriggerFilter: true, usableInCondition: true, group: 'Encounter' },
  { resourceType: 'Encounter', fieldPath: 'encounter.class', label: 'Class', description: 'Encounter class code (e.g. ambulatory)', valueType: 'code', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: true, usableInCondition: true, group: 'Encounter' },
  { resourceType: 'Encounter', fieldPath: 'encounter.locationId', label: 'Location id', description: 'Encounter.location Location reference id', valueType: 'string', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Encounter' },

  // Claim Assembly candidate graph (Claim trigger + related types)
  { resourceType: 'Claim', fieldPath: 'claim.type', label: 'Type', description: 'Claim type (professional or institutional)', valueType: 'code', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'professional', label: 'Professional' }, { value: 'institutional', label: 'Institutional' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Claim' },
  { resourceType: 'Claim', fieldPath: 'claim.use', label: 'Use', description: 'Claim use (claim, preauthorization, predetermination)', valueType: 'code', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'claim', label: 'Claim' }, { value: 'preauthorization', label: 'Preauthorization' }, { value: 'predetermination', label: 'Predetermination' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Claim' },
  { resourceType: 'Claim', fieldPath: 'claim.status', label: 'Status', description: 'Claim status', valueType: 'code', operators: ['equals', 'not-equals', 'in-list', 'not-in-list'], enumOptions: [{ value: 'draft', label: 'Draft' }, { value: 'active', label: 'Active' }, { value: 'cancelled', label: 'Cancelled' }, { value: 'entered-in-error', label: 'Entered in error' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Claim' },
  { resourceType: 'Claim', fieldPath: 'claim.provider', label: 'Billing provider', description: 'Whether Claim.provider is set', valueType: 'reference', operators: ['exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Claim' },
  { resourceType: 'ChargeItem', fieldPath: 'chargeItem.status', label: 'Status', description: 'ChargeItem status', valueType: 'code', operators: ['equals', 'not-equals', 'in-list', 'not-in-list'], enumOptions: [{ value: 'planned', label: 'Planned' }, { value: 'billable', label: 'Billable' }, { value: 'not-billable', label: 'Not billable' }, { value: 'aborted', label: 'Aborted' }, { value: 'billed', label: 'Billed' }, { value: 'entered-in-error', label: 'Entered in error' }, { value: 'unknown', label: 'Unknown' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Charge Item' },
  { resourceType: 'ChargeItem', fieldPath: 'chargeItem.code', label: 'Code', description: 'CPT/HCPCS on ChargeItem.code', valueType: 'coding', operators: ['equals', 'not-equals', 'in-list', 'exists', 'not-exists'], usableInTriggerFilter: true, usableInCondition: true, group: 'Charge Item' },
  { resourceType: 'ChargeItem', fieldPath: 'chargeItem.quantity', label: 'Quantity', description: 'ChargeItem.quantity value', valueType: 'number', operators: ['equals', 'not-equals', 'greater-than', 'less-than', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Charge Item' },
  { resourceType: 'ChargeItem', fieldPath: 'chargeItem.priceOverride', label: 'Price override', description: 'ChargeItem.priceOverride amount', valueType: 'number', operators: ['equals', 'not-equals', 'greater-than', 'less-than', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Charge Item' },
  { resourceType: 'Location', fieldPath: 'location.address.state', label: 'State', description: 'Location.address.state (USPS)', valueType: 'enum', operators: ['equals', 'not-equals', 'in-list', 'not-in-list', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Location' },
  { resourceType: 'Location', fieldPath: 'location.id', label: 'Id', description: 'Authoring only until Claim Assembly overlay evaluates this field. Location resource id.', valueType: 'string', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Location' },
  { resourceType: 'Location', fieldPath: 'location.name', label: 'Name', description: 'Authoring only until Claim Assembly overlay evaluates this field. Location.name.', valueType: 'string', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Location' },
  { resourceType: 'EpisodeOfCare', fieldPath: 'episode.paymentType', label: 'Payment type', description: 'Unify payment-type on EpisodeOfCare (insurance or self-pay)', valueType: 'code', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'insurance', label: 'Insurance' }, { value: 'self-pay', label: 'Self-pay' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Episode of Care' },
  { resourceType: 'Coverage', fieldPath: 'coverage.order', label: 'Order', description: 'Coverage.order (1 primary, 2 secondary, 3 tertiary)', valueType: 'number', operators: ['equals', 'not-equals', 'greater-than', 'less-than'], enumOptions: [{ value: '1', label: 'Primary (1)' }, { value: '2', label: 'Secondary (2)' }, { value: '3', label: 'Tertiary (3)' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Coverage' },
  { resourceType: 'Coverage', fieldPath: 'coverage.status', label: 'Status', description: 'Coverage status', valueType: 'code', operators: ['equals', 'not-equals', 'in-list'], enumOptions: [{ value: 'active', label: 'Active' }, { value: 'cancelled', label: 'Cancelled' }, { value: 'draft', label: 'Draft' }, { value: 'entered-in-error', label: 'Entered in error' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Coverage' },
  { resourceType: 'Coverage', fieldPath: 'coverage.payorId', label: 'Payor', description: 'Authoring only until Claim Assembly overlay evaluates this field. Coverage.payor Organization id (search payors; not type=prov).', valueType: 'string', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Coverage' },
  { resourceType: 'Coverage', fieldPath: 'coverage.relationship', label: 'Relationship', description: 'Coverage.relationship to subscriber', valueType: 'code', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'self', label: 'Self' }, { value: 'spouse', label: 'Spouse' }, { value: 'child', label: 'Child' }], usableInTriggerFilter: true, usableInCondition: true, group: 'Coverage' },
  { resourceType: 'Coverage', fieldPath: 'coverage.type', label: 'Type', description: 'Coverage.type code (e.g. MEDICARE, MEDICAID)', valueType: 'coding', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: true, usableInCondition: true, group: 'Coverage' },
  { resourceType: 'Organization', fieldPath: 'organization.npiExists', label: 'NPI exists', description: 'Whether the Organization has an NPI identifier', valueType: 'boolean', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }], usableInTriggerFilter: false, usableInCondition: true, group: 'Organization' },
  { resourceType: 'Organization', fieldPath: 'organization.type', label: 'Type', description: 'Organization.type code', valueType: 'coding', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: true, usableInCondition: true, group: 'Organization' },
  { resourceType: 'Practitioner', fieldPath: 'practitioner.npiExists', label: 'NPI exists', description: 'Whether the Practitioner has an NPI identifier', valueType: 'boolean', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }], usableInTriggerFilter: false, usableInCondition: true, group: 'Practitioner' },
  { resourceType: 'Practitioner', fieldPath: 'practitioner.npi', label: 'Rendering NPI', description: 'Authoring only until Claim Assembly overlay evaluates this field. NPI of the rendering practitioner on the candidate (not a named-clinician picker).', valueType: 'string', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Practitioner' },
  { resourceType: 'Practitioner', fieldPath: 'practitioner.supervisorNpi', label: 'Supervisor NPI', description: 'Authoring only until Claim Assembly overlay evaluates this field. NPI of the rendering practitioner’s supervisor (supervising-practitioner extension).', valueType: 'string', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Practitioner' },
  { resourceType: 'Practitioner', fieldPath: 'practitioner.taxonomy', label: 'Taxonomy', description: 'NUCC taxonomy on Practitioner.qualification', valueType: 'coding', operators: ['equals', 'not-equals', 'exists', 'not-exists'], usableInTriggerFilter: false, usableInCondition: true, group: 'Practitioner' },

  // Derived fields (cross-resource, computed at runtime)
  { resourceType: 'Patient', fieldPath: 'derived.hasValidRecipientEmail', label: 'Has valid recipient email', description: 'Whether a valid email exists for delivery', valueType: 'boolean', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }], usableInTriggerFilter: false, usableInCondition: true, group: 'Derived' },
  { resourceType: 'Appointment', fieldPath: 'derived.hasInPersonLocation', label: 'Has in-person location', description: 'Whether the appointment has an in-person (ROUTINE) modality and a resolvable Location participant', valueType: 'boolean', operators: ['equals', 'not-equals'], enumOptions: [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }], usableInTriggerFilter: false, usableInCondition: true, group: 'Derived' },
  { resourceType: 'Appointment', fieldPath: 'derived.incompleteFormTemplateIds', label: 'Required forms incomplete', description: 'Select forms to require; if any selected form has been assigned to the appointment\u2019s patient and is not yet completed, this condition is true. Forms never assigned to the patient do not block.', valueType: 'string', operators: ['any-of-incomplete'], usableInTriggerFilter: false, usableInCondition: true, group: 'Derived' },
  { resourceType: 'QuestionnaireResponse', fieldPath: 'derived.hoursSinceIntakeCompleted', label: 'Hours since intake completed', description: 'Time since intake was completed', valueType: 'number', operators: ['greater-than', 'less-than', 'past-hours'], usableInTriggerFilter: false, usableInCondition: true, group: 'Derived' },
  { resourceType: 'EpisodeOfCare', fieldPath: 'derived.hoursSinceEpisodeStarted', label: 'Hours since episode started', description: 'Time since episode started', valueType: 'number', operators: ['greater-than', 'less-than', 'past-hours'], usableInTriggerFilter: false, usableInCondition: true, group: 'Derived' },
];

export function getFieldsForResource(rt: TriggerResourceType): ResourceFieldDefinition[] {
  return RESOURCE_FIELD_REGISTRY.filter((f) => f.resourceType === rt);
}

export function getTriggerFilterFields(rt: TriggerResourceType): ResourceFieldDefinition[] {
  return RESOURCE_FIELD_REGISTRY.filter((f) => f.resourceType === rt && f.usableInTriggerFilter);
}

/**
 * Resources whose branch-condition fields are included when another type is the trigger.
 * Patient is always included separately. Derived fields follow their resourceType.
 */
export const TRIGGER_RELATED_RESOURCE_TYPES: Partial<Record<TriggerResourceType, TriggerResourceType[]>> = {
  Appointment: ['EpisodeOfCare', 'Encounter'],
  Encounter: ['Patient', 'EpisodeOfCare', 'Appointment'],
  EpisodeOfCare: ['Patient'],
  QuestionnaireResponse: ['Patient', 'EpisodeOfCare'],
  ServiceRequest: ['Patient', 'EpisodeOfCare'],
  Task: ['Patient', 'EpisodeOfCare'],
  Communication: ['Patient'],
  CommunicationRequest: ['Patient'],
  CareTeam: ['Patient'],
  Patient: [],
  Claim: ['ChargeItem', 'Encounter', 'Location', 'EpisodeOfCare', 'Coverage', 'Organization', 'Practitioner', 'Appointment'],
  ChargeItem: ['Encounter', 'Claim'],
};

/** Resource types available for branch conditions for the given trigger selection. */
export function getConditionFieldScope(resourceTypes?: TriggerResourceType[]): Set<TriggerResourceType> {
  const scope = new Set<TriggerResourceType>();
  if (!resourceTypes?.length) {
    for (const f of RESOURCE_FIELD_REGISTRY) {
      if (f.usableInCondition || f.usableInTriggerFilter) {
        scope.add(f.resourceType);
      }
    }
    return scope;
  }
  for (const rt of resourceTypes) {
    scope.add(rt);
    for (const related of TRIGGER_RELATED_RESOURCE_TYPES[rt] ?? []) {
      scope.add(related);
    }
  }
  return scope;
}

export function getConditionFields(resourceTypes?: TriggerResourceType[]): ResourceFieldDefinition[] {
  const scope = getConditionFieldScope(resourceTypes);
  return RESOURCE_FIELD_REGISTRY.filter((f) => {
    if (!(f.usableInCondition || f.usableInTriggerFilter)) return false;
    if (f.resourceType === 'Patient') return true;
    if (f.group === 'Derived') return true;
    return scope.has(f.resourceType);
  });
}

// ---------------------------------------------------------------------------
// V2 Trigger Resource Filters (replaces V1 object filters)
// ---------------------------------------------------------------------------

export interface ResourceFieldFilter {
  id: string;
  fieldPath: string;
  operator: ConditionOperator;
  value: string;
  /** Optional system for coding-type fields. */
  system?: string;
  /** Optional display label for the selected value. */
  label?: string;
}

// ---------------------------------------------------------------------------
// Trigger Presets — lifecycle shortcuts that populate resource+operation+filters
// ---------------------------------------------------------------------------

export type TriggerPreset =
  | 'client-created'
  | 'intake-completed'
  | 'episode-of-care-requested'
  | 'episode-of-care-started'
  | 'email-template-activated'
  | 'manual-rule-run';

export interface TriggerPresetDefinition {
  value: TriggerPreset;
  label: string;
  description: string;
  resourceType: TriggerResourceType;
  operation: TriggerOperation;
  defaultFilters: ResourceFieldFilter[];
}

export const TRIGGER_PRESETS: TriggerPresetDefinition[] = [
  {
    value: 'client-created',
    label: 'Client created',
    description: 'New patient/client created',
    resourceType: 'Patient',
    operation: 'created',
    defaultFilters: [
      { id: 'preset-patient-active', fieldPath: 'patient.active', operator: 'equals', value: 'true', label: 'Patient is active' },
    ],
  },
  {
    value: 'intake-completed',
    label: 'Intake completed',
    description: 'Client completes intake questionnaire',
    resourceType: 'QuestionnaireResponse',
    operation: 'created',
    defaultFilters: [
      { id: 'preset-qr-status', fieldPath: 'status', operator: 'equals', value: 'completed', label: 'Status is completed' },
    ],
  },
  {
    value: 'episode-of-care-requested',
    label: 'Episode of Care requested',
    description: 'New Episode of Care request created',
    resourceType: 'ServiceRequest',
    operation: 'created',
    defaultFilters: [
      { id: 'preset-sr-code', fieldPath: 'code.coding', operator: 'equals', value: 'new-episode-of-care', system: 'https://unify.health/fhir/CodeSystem/service-request-type', label: 'Code is new-episode-of-care' },
    ],
  },
  {
    value: 'episode-of-care-started',
    label: 'Episode of Care started',
    description: 'Episode of Care becomes active',
    resourceType: 'EpisodeOfCare',
    operation: 'updated',
    defaultFilters: [
      { id: 'preset-eoc-status', fieldPath: 'episode.status', operator: 'equals', value: 'active', label: 'Status is active' },
    ],
  },
  {
    value: 'email-template-activated',
    label: 'Email template activated',
    description: 'Email notification template is activated',
    resourceType: 'ActivityDefinition',
    operation: 'updated',
    defaultFilters: [
      { id: 'preset-ad-status', fieldPath: 'activityDefinition.status', operator: 'equals', value: 'active', label: 'Status is active' },
    ],
  },
  {
    value: 'manual-rule-run',
    label: 'Manual rule run',
    description: 'Rule triggered manually via Task',
    resourceType: 'Task',
    operation: 'manual-run',
    defaultFilters: [
      { id: 'preset-task-code', fieldPath: 'task.code', operator: 'equals', value: 'manual-rule-run', system: 'https://unify.health/fhir/CodeSystem/rules-engine', label: 'Code is manual-rule-run' },
    ],
  },
];

// ---------------------------------------------------------------------------
// V1 Legacy Trigger Types — kept for backward compatibility / migration
// ---------------------------------------------------------------------------

/** @deprecated Use TriggerOperation. Kept for V1 PlanDefinition parsing. */
export type TriggerEventKind =
  | 'client-created'
  | 'client-intake-completed'
  | 'episode-of-care-requested'
  | 'episode-of-care-started'
  | 'manual-rule-run';

/** @deprecated Use TRIGGER_OPERATIONS. Kept for V1 migration. */
export const TRIGGER_EVENT_KINDS: { value: TriggerEventKind; label: string }[] = [
  { value: 'client-created', label: 'Client created' },
  { value: 'client-intake-completed', label: 'Client intake completed' },
  { value: 'episode-of-care-requested', label: 'New Episode of Care requested' },
  { value: 'episode-of-care-started', label: 'Episode of Care started' },
  { value: 'manual-rule-run', label: 'Manual rule run' },
];

/** @deprecated Maps V1 lifecycle events to resource+operation. Used during migration. */
export const V1_EVENT_TO_GENERIC: Record<TriggerEventKind, { resourceType: TriggerResourceType; operation: TriggerOperation }> = {
  'client-created': { resourceType: 'Patient', operation: 'created' },
  'client-intake-completed': { resourceType: 'QuestionnaireResponse', operation: 'created' },
  'episode-of-care-requested': { resourceType: 'ServiceRequest', operation: 'created' },
  'episode-of-care-started': { resourceType: 'EpisodeOfCare', operation: 'updated' },
  'manual-rule-run': { resourceType: 'Task', operation: 'manual-run' },
};

/** @deprecated Maps V1 generic operation back to event kind for runtime compat. */
export const GENERIC_TO_V1_EVENT: Record<string, TriggerEventKind> = {
  'Patient|created': 'client-created',
  'QuestionnaireResponse|created': 'client-intake-completed',
  'ServiceRequest|created': 'episode-of-care-requested',
  'EpisodeOfCare|updated': 'episode-of-care-started',
  'Task|manual-run': 'manual-rule-run',
};

// ---------------------------------------------------------------------------
// V1 Trigger Object Filters — kept for migration compat
// ---------------------------------------------------------------------------

export type TriggerFilterKind =
  | 'questionnaire-canonical'
  | 'questionnaire-id'
  | 'service-request-code'
  | 'service-request-category'
  | 'episode-type'
  | 'task-code';

export interface TriggerObjectFilter {
  id: string;
  kind: TriggerFilterKind;
  system?: string;
  code?: string;
  value?: string;
  label?: string;
}

/** @deprecated Use RESOURCE_TYPE_REGISTRY operations. */
export const TRIGGER_EVENTS_BY_RESOURCE: Record<string, TriggerEventKind[]> = {
  Patient: ['client-created'],
  QuestionnaireResponse: ['client-intake-completed'],
  ServiceRequest: ['episode-of-care-requested'],
  EpisodeOfCare: ['episode-of-care-started'],
  Task: ['manual-rule-run'],
};

export const EPISODE_OF_CARE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'individual', label: 'Individual Therapy' },
  { value: 'couples', label: 'Couples Therapy' },
  { value: 'family', label: 'Family Therapy' },
  { value: 'group', label: 'Group Therapy' },
  { value: 'iop', label: 'Intensive Outpatient Program' },
  { value: 'medication-management', label: 'Medication Management' },
  { value: 'psych-testing', label: 'Psychological Testing' },
];

export const TRIGGER_FILTER_KINDS_BY_RESOURCE: Record<string, TriggerFilterKind[]> = {
  QuestionnaireResponse: ['questionnaire-canonical', 'questionnaire-id'],
  ServiceRequest: ['service-request-code', 'service-request-category'],
  EpisodeOfCare: ['episode-type'],
  Task: ['task-code'],
  Patient: [],
};

export const TRIGGER_FILTER_KIND_LABELS: Record<TriggerFilterKind, string> = {
  'questionnaire-canonical': 'Questionnaire canonical URL',
  'questionnaire-id': 'Questionnaire ID',
  'service-request-code': 'ServiceRequest code',
  'service-request-category': 'ServiceRequest category',
  'episode-type': 'Episode of Care type',
  'task-code': 'Task code',
};

export const SERVICE_REQUEST_TYPE_SYSTEM =
  'https://unify.health/fhir/CodeSystem/service-request-type';
export const SERVICE_REQUEST_NEW_EPISODE_CODE = 'new-episode-of-care';

export const SERVICE_REQUEST_CATEGORY_SYSTEM_LEGACY =
  'https://unify.health/fhir/CodeSystem/service-request-category';
export const SERVICE_REQUEST_CATEGORY_CODE_LEGACY = 'service-request';

export const RULES_ENGINE_CODE_SYSTEM =
  'https://unify.health/fhir/CodeSystem/rules-engine';
export const MANUAL_RUN_TASK_CODE = 'manual-rule-run';

/** @deprecated Use field registry REQUIRED_TRIGGER_FILTERS. Kept for V1 compat. */
export const REQUIRED_TRIGGER_FILTERS: Record<TriggerEventKind, Partial<Record<string, TriggerFilterKind[]>>> = {
  'client-created': {},
  'client-intake-completed': {
    QuestionnaireResponse: ['questionnaire-canonical', 'questionnaire-id'],
  },
  'episode-of-care-requested': {
    ServiceRequest: ['service-request-code', 'service-request-category'],
  },
  'episode-of-care-started': {
    EpisodeOfCare: ['episode-type'],
  },
  'manual-rule-run': {
    Task: ['task-code'],
  },
};

export type TriggerMode = 'real-time' | 'batch' | 'both';

export const TRIGGER_MODES: { value: TriggerMode; label: string }[] = [
  { value: 'real-time', label: 'Real-time' },
  { value: 'batch', label: 'Batch / scheduled' },
  { value: 'both', label: 'Both' },
];

// ---------------------------------------------------------------------------
// Action Types
// ---------------------------------------------------------------------------

export type ActionType = 'send-notification' | 'send-email' | 'assign-template-forms' | 'set-claim-field';

export const ACTION_TYPES: { value: ActionType; label: string; description: string }[] = [
  { value: 'send-notification', label: 'Send in-app notification', description: 'Send an in-app notification to the client or care team' },
  { value: 'send-email', label: 'Send email', description: 'Send an email to the client or care team via the notification-email bot' },
  { value: 'assign-template-forms', label: 'Assign forms', description: 'Assign one or more Template Library forms to the client' },
  { value: 'set-claim-field', label: 'Set Claim field', description: 'Overlay a field on the Claim draft during claim assembly' },
];

export const NOTIFICATION_ACTION_TYPES: ActionType[] = [
  'send-notification',
  'send-email',
  'assign-template-forms',
];

export function allowedActionTypes(domain: RulePackDomain): ActionType[] {
  return isClaimAssemblyDomain(domain) ? ['set-claim-field'] : NOTIFICATION_ACTION_TYPES;
}

export function defaultActionTypeForDomain(domain: RulePackDomain): ActionType {
  return allowedActionTypes(domain)[0];
}

export type ClaimFieldValueSource = 'literal' | 'graph';

export type ClaimReferenceResourceType = 'Organization' | 'Practitioner' | 'Location';

/** CMS Place of Service Code Set (persist as Coding.system). */
export const CLAIM_POS_SYSTEM = CMS_PLACE_OF_SERVICE_CODE_SYSTEM;

/** AMA CPT modifiers (persist as Coding.system). */
export const CLAIM_MODIFIER_SYSTEM = CPT_CODE_SYSTEM;

/** Unify supportingInfo category for additional claim info overlay. */
export const CLAIM_ADDITIONAL_INFO_CATEGORY = 'additional-claim-info';

export const CLAIM_POS_OPTIONS: { value: string; label: string }[] = [
  { value: '02', label: '02 Telehealth (other than home)' },
  { value: '10', label: "10 Telehealth in patient's home" },
  { value: '11', label: '11 Office' },
  { value: '12', label: '12 Home' },
  { value: '13', label: '13 Assisted living facility' },
  { value: '14', label: '14 Group home' },
  { value: '15', label: '15 Mobile unit' },
  { value: '19', label: '19 Off campus-outpatient hospital' },
  { value: '20', label: '20 Urgent care facility' },
  { value: '21', label: '21 Inpatient hospital' },
  { value: '22', label: '22 On campus-outpatient hospital' },
  { value: '23', label: '23 Emergency room-hospital' },
  { value: '24', label: '24 Ambulatory surgical center' },
  { value: '31', label: '31 Skilled nursing facility' },
  { value: '32', label: '32 Nursing facility' },
  { value: '33', label: '33 Custodial care facility' },
  { value: '49', label: '49 Independent clinic' },
  { value: '50', label: '50 Federally qualified health center' },
  { value: '51', label: '51 Inpatient psychiatric facility' },
  { value: '52', label: '52 Psychiatric facility-partial hospitalization' },
  { value: '53', label: '53 Community mental health center' },
  { value: '55', label: '55 Residential substance abuse treatment facility' },
  { value: '56', label: '56 Psychiatric residential treatment center' },
  { value: '57', label: '57 Non-residential substance abuse treatment facility' },
  { value: '71', label: '71 Public health clinic' },
  { value: '72', label: '72 Rural health clinic' },
  { value: '99', label: '99 Other place of service' },
];

export const CLAIM_MODIFIER_OPTIONS: { value: string; label: string }[] = [
  { value: '95', label: '95 Synchronous telemedicine' },
  { value: 'GT', label: 'GT Via interactive audio and video' },
  { value: '93', label: '93 Synchronous telemedicine via telephone' },
  { value: 'HO', label: "HO Master's degree level" },
  { value: 'HN', label: "HN Bachelor's degree level" },
  { value: 'HM', label: 'HM Less than bachelor degree level' },
  { value: 'HP', label: 'HP Doctoral level' },
  { value: 'HK', label: 'HK Specialized mental health programs' },
  { value: 'AJ', label: 'AJ Clinical social worker' },
  { value: 'AF', label: 'AF Specialty physician' },
  { value: 'AG', label: 'AG Primary physician' },
  { value: 'SA', label: 'SA Nurse practitioner with physician' },
  { value: '25', label: '25 Significant, separately identifiable E/M' },
  { value: '59', label: '59 Distinct procedural service' },
  { value: '76', label: '76 Repeat procedure by same physician' },
  { value: '77', label: '77 Repeat procedure by another physician' },
  { value: '52', label: '52 Reduced services' },
  { value: '53', label: '53 Discontinued procedure' },
];

export interface ClaimGraphValueDefinition {
  token: string;
  label: string;
  valueType: 'reference';
  referenceResourceType: ClaimReferenceResourceType;
}

/**
 * Graph write tokens. Singular targets fail closed at overlay if 0 or >1 distinct
 * source refs; identical refs across members count as one. Per-item graph copies
 * (none this slice) must use sourceChargeItem.
 */
export const CLAIM_GRAPH_VALUE_PATHS: ClaimGraphValueDefinition[] = [
  { token: 'rendering.practitioner', label: 'Rendering practitioner', valueType: 'reference', referenceResourceType: 'Practitioner' },
  { token: 'rendering.supervisor', label: 'Rendering provider supervisor', valueType: 'reference', referenceResourceType: 'Practitioner' },
  { token: 'encounter.location', label: 'Encounter location', valueType: 'reference', referenceResourceType: 'Location' },
];

export const CLAIM_GRAPH_WRITE_TOKENS = new Set(CLAIM_GRAPH_VALUE_PATHS.map((t) => t.token));

export function getClaimGraphValue(token: string): ClaimGraphValueDefinition | undefined {
  return CLAIM_GRAPH_VALUE_PATHS.find((t) => t.token === token);
}

export interface ClaimWriteFieldDefinition {
  fieldPath: string;
  label: string;
  description: string;
  valueType: FieldValueType;
  enumOptions?: { value: string; label: string }[];
  referenceResourceType?: ClaimReferenceResourceType;
  allowedGraphTokens?: string[];
  codingSystem?: string;
  multi?: boolean;
}

export const CLAIM_WRITE_FIELDS: ClaimWriteFieldDefinition[] = [
  { fieldPath: 'claim.provider', label: 'Billing provider', description: 'Organization reference for Claim.provider', valueType: 'reference', referenceResourceType: 'Organization' },
  { fieldPath: 'claim.type', label: 'Claim type', description: 'professional or institutional. Overlay must match group shape (fail closed when evaluator ships).', valueType: 'code', enumOptions: [{ value: 'professional', label: 'Professional' }, { value: 'institutional', label: 'Institutional' }] },
  { fieldPath: 'claim.use', label: 'Claim use', description: 'claim, preauthorization, or predetermination', valueType: 'code', enumOptions: [{ value: 'claim', label: 'Claim' }, { value: 'preauthorization', label: 'Preauthorization' }, { value: 'predetermination', label: 'Predetermination' }] },
  { fieldPath: 'claim.careTeam.primary', label: 'Rendering practitioner', description: 'Upsert Claim.careTeam provider for role primary. Sequence is owned by assemble.', valueType: 'reference', referenceResourceType: 'Practitioner', allowedGraphTokens: ['rendering.practitioner', 'rendering.supervisor'] },
  { fieldPath: 'claim.careTeam.supervisor', label: 'Supervising practitioner', description: 'Upsert Claim.careTeam provider for role supervisor. UI may say supervisor NPI; graph stores the Practitioner ref.', valueType: 'reference', referenceResourceType: 'Practitioner', allowedGraphTokens: ['rendering.practitioner', 'rendering.supervisor'] },
  { fieldPath: 'claim.facility', label: 'Service facility', description: 'Location reference for Claim.facility', valueType: 'reference', referenceResourceType: 'Location', allowedGraphTokens: ['encounter.location'] },
  { fieldPath: 'claim.item.locationCodeableConcept', label: 'Place of service', description: 'POS Coding on all Claim.item lines', valueType: 'coding', codingSystem: CLAIM_POS_SYSTEM, enumOptions: CLAIM_POS_OPTIONS },
  { fieldPath: 'claim.item.modifier', label: 'Procedure modifiers', description: 'Replace modifier Coding list on all Claim.item lines', valueType: 'coding', codingSystem: CLAIM_MODIFIER_SYSTEM, enumOptions: CLAIM_MODIFIER_OPTIONS, multi: true },
  { fieldPath: 'claim.item.productOrService', label: 'Procedure code', description: 'CPT/HCPCS on all lines (same code). Does not copy ChargeItem.code per line.', valueType: 'coding' },
  { fieldPath: 'claim.item.quantity', label: 'Quantity', description: 'Quantity.value on all Claim.item lines (unit 1)', valueType: 'number' },
  { fieldPath: 'claim.item.diagnosisSequence', label: 'Diagnosis sequence', description: 'diagnosisSequence on all Claim.item lines (e.g. 1 or 1,2)', valueType: 'string' },
  { fieldPath: 'claim.item.servicedDate', label: 'Service date', description: 'servicedDate on all Claim.item lines (YYYY-MM-DD)', valueType: 'date' },
  { fieldPath: 'claim.item.careTeamSequence', label: 'Care team sequence', description: 'careTeamSequence on all Claim.item lines (e.g. 1 or 1,2)', valueType: 'string' },
  { fieldPath: 'claim.insurance.preAuthRef', label: 'Prior authorization', description: 'preAuthRef on focal insurance only; does not retarget Coverage', valueType: 'string' },
  { fieldPath: 'claim.supportingInfo.additionalInfo', label: 'Additional claim info', description: `Replace supportingInfo entries with category ${CLAIM_ADDITIONAL_INFO_CATEGORY} only`, valueType: 'string' },
];

export const CLAIM_WRITE_FIELD_PATHS = new Set(CLAIM_WRITE_FIELDS.map((f) => f.fieldPath));

export function getClaimWriteField(fieldPath: string): ClaimWriteFieldDefinition | undefined {
  return CLAIM_WRITE_FIELDS.find((f) => f.fieldPath === fieldPath);
}

export function graphTokensForClaimField(fieldPath: string): ClaimGraphValueDefinition[] {
  const allowed = getClaimWriteField(fieldPath)?.allowedGraphTokens;
  if (!allowed?.length) return [];
  return CLAIM_GRAPH_VALUE_PATHS.filter((t) => allowed.includes(t.token));
}

export function isClaimGraphTokenCompatible(fieldPath: string, token: string): boolean {
  return graphTokensForClaimField(fieldPath).some((t) => t.token === token);
}

// ---------------------------------------------------------------------------
// Condition operators & fields
// ---------------------------------------------------------------------------

export type ConditionOperator =
  | 'equals'
  | 'not-equals'
  | 'exists'
  | 'not-exists'
  | 'greater-than'
  | 'less-than'
  | 'in-list'
  | 'not-in-list'
  | 'past-hours'
  | 'any-of-incomplete';

export const CONDITION_OPERATORS: { value: ConditionOperator; label: string }[] = [
  { value: 'equals', label: 'equals' },
  { value: 'not-equals', label: 'does not equal' },
  { value: 'exists', label: 'exists' },
  { value: 'not-exists', label: 'does not exist' },
  { value: 'greater-than', label: 'is greater than' },
  { value: 'less-than', label: 'is less than' },
  { value: 'in-list', label: 'is in list' },
  { value: 'not-in-list', label: 'is not in list' },
  { value: 'past-hours', label: 'is past N hours' },
  { value: 'any-of-incomplete', label: 'any selected form is incomplete' },
];

/**
 * Condition field — now uses field registry paths. Legacy dot-paths are
 * kept as string union for backward compatibility with V1 stored rules.
 */
export type ConditionField = string;

/**
 * @deprecated Use RESOURCE_FIELD_REGISTRY for grouped condition field data.
 * Kept for V1 backward compat — old stored rules reference these paths.
 */
export const CONDITION_FIELDS: { value: string; label: string; category: string }[] = [
  { value: 'patient.active', label: 'Patient active', category: 'Patient' },
  { value: 'patient.email.exists', label: 'Patient has email', category: 'Patient' },
  { value: 'patient.communicationPreference.emailAllowed', label: 'Email allowed (comm prefs)', category: 'Patient' },
  { value: 'patient.age', label: 'Patient age', category: 'Patient' },
  { value: 'patient.address.state', label: 'Patient state', category: 'Patient' },
  { value: 'intake.status', label: 'Intake status', category: 'Questionnaire Response' },
  { value: 'intake.submittedAt', label: 'Intake submitted at', category: 'Questionnaire Response' },
  { value: 'intake.completedAt', label: 'Intake completed at', category: 'Questionnaire Response' },
  { value: 'episode.status', label: 'Episode status', category: 'Episode of Care' },
  { value: 'episode.startedAt', label: 'Episode started at', category: 'Episode of Care' },
  { value: 'episode.type', label: 'Episode type', category: 'Episode of Care' },
  { value: 'serviceRequest.status', label: 'Service request status', category: 'Service Request' },
  { value: 'derived.hasValidRecipientEmail', label: 'Has valid recipient email', category: 'Derived' },
  { value: 'derived.hoursSinceIntakeCompleted', label: 'Hours since intake completed', category: 'Derived' },
  { value: 'derived.hoursSinceEpisodeStarted', label: 'Hours since episode started', category: 'Derived' },
  { value: 'derived.wasPreviouslySent', label: 'Was previously sent', category: 'Derived' },
  { value: 'derived.wasPreviouslyAssigned', label: 'Was previously assigned', category: 'Derived' },
  // New V2 fields from registry
  { value: 'status', label: 'Status', category: 'Questionnaire Response' },
  { value: 'questionnaire', label: 'Questionnaire', category: 'Questionnaire Response' },
  { value: 'code.coding', label: 'Code', category: 'Service Request' },
  { value: 'category.coding', label: 'Category', category: 'Service Request' },
  { value: 'task.status', label: 'Task status', category: 'Task' },
  { value: 'task.code', label: 'Task code', category: 'Task' },
  { value: 'communication.status', label: 'Communication status', category: 'Communication' },
  { value: 'communication.medium', label: 'Communication medium', category: 'Communication' },
  { value: 'communication.category', label: 'Communication category', category: 'Communication' },
  { value: 'activityDefinition.status', label: 'Template status', category: 'Activity Definition' },
  { value: 'activityDefinition.name', label: 'Template name', category: 'Activity Definition' },
  { value: 'questionnaire.status', label: 'Questionnaire status', category: 'Questionnaire' },
  { value: 'questionnaire.url', label: 'Questionnaire URL', category: 'Questionnaire' },
];

/**
 * Build grouped condition field data from the field registry, scoped to
 * the trigger resource type and commonly related resources.
 */
export function buildConditionFieldGroupedData(
  triggerResourceTypes?: TriggerResourceType[],
): { group: string; items: { value: string; label: string }[] }[] {
  const fields = getConditionFields(triggerResourceTypes);
  const byGroup = new Map<string, { value: string; label: string }[]>();
  for (const f of fields) {
    const groupKey = f.group === 'Derived' ? 'Derived' : f.resourceType;
    const dotLabel = f.group === 'Derived'
      ? `Derived.${toCamelCase(f.label)}`
      : `${f.resourceType}.${toCamelCase(f.label)}`;
    const list = byGroup.get(groupKey) ?? [];
    list.push({ value: f.fieldPath, label: dotLabel });
    byGroup.set(groupKey, list);
  }
  return Array.from(byGroup.entries()).map(([group, items]) => ({ group, items }));
}

function toCamelCase(s: string): string {
  const words = s.split(/\s+/);
  return words
    .map((w, i) => (i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1)))
    .join('');
}

export type CommunicationChannel = 'in-app' | 'email';

export const COMMUNICATION_CHANNELS: { value: CommunicationChannel; label: string }[] = [
  { value: 'in-app', label: 'In-app notification' },
  { value: 'email', label: 'Email' },
];

export type ConflictPolicy = 'first-match' | 'all-match';

export const CONFLICT_POLICIES: { value: ConflictPolicy; label: string }[] = [
  { value: 'first-match', label: 'First match wins' },
  { value: 'all-match', label: 'Execute all matching' },
];

// ---------------------------------------------------------------------------
// Action Key — deprecated, kept for V1 compat
// ---------------------------------------------------------------------------

/**
 * @deprecated Action keys are removed from new rules. Old rules with action keys
 * continue to parse. Use action.id + action.type for dedupe/audit instead.
 */
export type RuleActionKey =
  | 'notify.client_intake_completed'
  | 'notify.episode_requested'
  | 'notify.episode_started'
  | 'email.client_intake_completed'
  | 'email.episode_requested'
  | 'email.episode_started'
  | 'email.client_created'
  | 'assign_forms.after_intake_completed'
  | 'assign_forms.after_episode_requested'
  | 'assign_forms.after_episode_started'
  | 'assign_forms.on_client_created'
  | 'assign_forms.manual';

/** @deprecated Use action.id + action.type for new rules. */
export const RULE_ACTION_KEYS: { value: RuleActionKey; label: string; actionType: ActionType }[] = [
  { value: 'notify.client_intake_completed', label: 'Notify after intake completed', actionType: 'send-notification' },
  { value: 'notify.episode_requested', label: 'Notify when episode requested', actionType: 'send-notification' },
  { value: 'notify.episode_started', label: 'Notify when episode started', actionType: 'send-notification' },
  { value: 'email.client_intake_completed', label: 'Email after intake completed', actionType: 'send-email' },
  { value: 'email.episode_requested', label: 'Email when episode requested', actionType: 'send-email' },
  { value: 'email.episode_started', label: 'Email when episode started', actionType: 'send-email' },
  { value: 'email.client_created', label: 'Email when client is created', actionType: 'send-email' },
  { value: 'assign_forms.after_intake_completed', label: 'Assign forms after intake completed', actionType: 'assign-template-forms' },
  { value: 'assign_forms.after_episode_requested', label: 'Assign forms when episode requested', actionType: 'assign-template-forms' },
  { value: 'assign_forms.after_episode_started', label: 'Assign forms when episode started', actionType: 'assign-template-forms' },
  { value: 'assign_forms.on_client_created', label: 'Assign forms when client is created', actionType: 'assign-template-forms' },
  { value: 'assign_forms.manual', label: 'Assign forms (manual)', actionType: 'assign-template-forms' },
];

// ---------------------------------------------------------------------------
// Template Library Reference
// ---------------------------------------------------------------------------

export interface TemplateLibraryRef {
  id: string;
  title: string;
  category?: string;
  type?: 'form' | 'document' | string;
  canonicalUrl?: string;
  version?: string;
}

// ---------------------------------------------------------------------------
// Recipient Policy
// ---------------------------------------------------------------------------

export type RecipientPolicy =
  | 'client-primary'
  | 'client-primary-email'
  | 'guardian-or-responsible-party'
  | 'billing-contact'
  | 'contact-form-submitter'
  | 'care-team-primary-contact'
  | 'assigned-provider'
  | 'unify-admin'
  | 'appointment-participants';

export function normalizeRecipientPolicy(policy: RecipientPolicy): RecipientPolicy {
  if (policy === 'client-primary-email') return 'client-primary';
  return policy;
}

export interface RecipientPolicyOption {
  value: RecipientPolicy;
  label: string;
  description: string;
  implementedForNotification: boolean;
  implementedForEmail: boolean;
  implementedForAssignment: boolean;
}

export const RECIPIENT_POLICIES: RecipientPolicyOption[] = [
  { value: 'client-primary', label: 'Client', description: 'The patient/client', implementedForNotification: true, implementedForEmail: true, implementedForAssignment: true },
  {
    value: 'appointment-participants',
    label: 'Appointment participants',
    description:
      'All Patient and RelatedPerson participants on the triggering Appointment (excludes practitioners, rooms, and devices)',
    implementedForNotification: true,
    implementedForEmail: true,
    implementedForAssignment: false,
  },
  { value: 'care-team-primary-contact', label: 'Care team primary contact', description: 'Primary contact from the care team', implementedForNotification: true, implementedForEmail: true, implementedForAssignment: false },
  { value: 'guardian-or-responsible-party', label: 'Guardian / responsible party', description: 'Guardian or responsible party — resolves from RelatedPerson', implementedForNotification: true, implementedForEmail: true, implementedForAssignment: false },
  { value: 'unify-admin', label: 'Unify Admin', description: 'Configured admin Practitioner recipients (deferred — requires project-level config)', implementedForNotification: false, implementedForEmail: false, implementedForAssignment: false },
  { value: 'billing-contact', label: 'Billing contact', description: 'The billing contact on file (not implemented)', implementedForNotification: false, implementedForEmail: false, implementedForAssignment: false },
  { value: 'contact-form-submitter', label: 'Contact form submitter', description: 'From the original contact form submission (not implemented)', implementedForNotification: false, implementedForEmail: false, implementedForAssignment: false },
  { value: 'assigned-provider', label: 'Assigned provider', description: 'The Practitioner participant on the triggering Appointment (Appointment-triggered rules only)', implementedForNotification: true, implementedForEmail: false, implementedForAssignment: false },
];

export const NOTIFICATION_RECIPIENT_POLICIES = RECIPIENT_POLICIES.filter((p) => p.implementedForNotification);
export const EMAIL_RECIPIENT_POLICIES = RECIPIENT_POLICIES.filter((p) => p.implementedForEmail);
export const FORM_ASSIGNMENT_RECIPIENT_POLICIES = RECIPIENT_POLICIES.filter((p) => p.implementedForAssignment);

// ---------------------------------------------------------------------------
// Timing
// ---------------------------------------------------------------------------

export type TimingBucket = 'immediate' | 'scheduled';

export const TIMING_BUCKETS: { value: TimingBucket; label: string }[] = [
  { value: 'immediate', label: 'Immediate' },
  { value: 'scheduled', label: 'Scheduled' },
];

/** @deprecated Non-immediate timing buckets are not yet supported. */
export type LegacyTimingBucket =
  | 'immediate'
  | 'scheduled'
  | '1h'
  | '4h'
  | '24h'
  | '48h'
  | '72h'
  | '7d';

// ---------------------------------------------------------------------------
// Scheduled action timing — offset relative to a resource datetime field
// ---------------------------------------------------------------------------

export type ScheduledTimingDirection = 'before' | 'after' | 'at';
export type ScheduledTimingUnit = 'minutes' | 'hours' | 'days';

export interface ActionScheduledTiming {
  direction: ScheduledTimingDirection;
  offsetAmount: number;
  offsetUnit: ScheduledTimingUnit;
  /** Dot-path to the datetime field on the trigger resource (e.g. `start`, `period.start`). */
  referenceField: string;
}

export const SCHEDULED_TIMING_DIRECTIONS: { value: ScheduledTimingDirection; label: string }[] = [
  { value: 'before', label: 'Before' },
  { value: 'after', label: 'After' },
  { value: 'at', label: 'At' },
];

export const SCHEDULED_TIMING_UNITS: { value: ScheduledTimingUnit; label: string }[] = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

export interface ScheduledTimingReferenceField {
  resourceType: TriggerResourceType;
  fieldPath: string;
  label: string;
}

export const SCHEDULED_TIMING_REFERENCE_FIELDS: ScheduledTimingReferenceField[] = [
  { resourceType: 'Appointment', fieldPath: 'start', label: 'Start time' },
  { resourceType: 'Appointment', fieldPath: 'end', label: 'End time' },
  { resourceType: 'EpisodeOfCare', fieldPath: 'period.start', label: 'Period start' },
  { resourceType: 'EpisodeOfCare', fieldPath: 'period.end', label: 'Period end' },
  { resourceType: 'Encounter', fieldPath: 'period.start', label: 'Period start' },
  { resourceType: 'Encounter', fieldPath: 'period.end', label: 'Period end' },
  { resourceType: 'Task', fieldPath: 'authoredOn', label: 'Authored on' },
  { resourceType: 'Task', fieldPath: 'restriction.period.end', label: 'Due date' },
  { resourceType: 'ServiceRequest', fieldPath: 'authoredOn', label: 'Authored on' },
  { resourceType: 'Communication', fieldPath: 'sent', label: 'Sent' },
  { resourceType: 'Patient', fieldPath: 'meta.lastUpdated', label: 'Last updated' },
  { resourceType: 'QuestionnaireResponse', fieldPath: 'authored', label: 'Authored' },
];

export function getScheduledTimingFieldsForResource(rt: TriggerResourceType): ScheduledTimingReferenceField[] {
  return SCHEDULED_TIMING_REFERENCE_FIELDS.filter((f) => f.resourceType === rt);
}

export const SCHEDULED_ACTION_TASK_CODE = 'scheduled-action';
export const SCHEDULED_ACTION_TASK_CODING = {
  system: RULES_ENGINE_CODE_SYSTEM,
  code: SCHEDULED_ACTION_TASK_CODE,
  display: 'Scheduled action',
} as const;

// ---------------------------------------------------------------------------
// Draft / editor types
// ---------------------------------------------------------------------------

export interface RuleCondition {
  id: string;
  field: ConditionField;
  operator: ConditionOperator;
  value: string;
  logicalOperator?: 'AND' | 'OR';
}

export interface ConditionGroup {
  id: string;
  conditions: RuleCondition[];
  groupOperator: 'AND' | 'OR';
}

export interface RuleAction {
  id: string;
  type: ActionType;
  label: string;
  description?: string;

  /**
   * @deprecated Action keys are not required for new rules. Old rules with
   * action keys continue to parse. Use action.id + action.type instead.
   */
  actionKey?: RuleActionKey;
  /** @deprecated Stored for old-rule compat when actionKey was present. */
  legacyActionKey?: string;

  // Notification action fields
  notificationRecipientPolicies?: RecipientPolicy[];
  notificationTitle?: string;
  notificationBody?: string;
  /** Preference category for in-app delivery guard (e.g. episode-transfers, forms-submitted). */
  notificationCategory?: string;

  // Email action fields
  emailRecipientPolicies?: RecipientPolicy[];
  emailTemplateRef?: TemplateLibraryRef;

  // Form/template assignment action fields
  formTemplateRefs?: TemplateLibraryRef[];
  assignmentRecipientPolicies?: RecipientPolicy[];
  assignmentDueOffsetDays?: number;
  assignmentMessageTemplateRef?: TemplateLibraryRef;

  // Claim Assembly overlay
  claimFieldPath?: string;
  claimFieldValue?: string;
  claimFieldSystem?: string;
  /** Omit or `literal` on old rules. Search pickers persist as literal refs. */
  claimFieldValueSource?: ClaimFieldValueSource;

  // Shared
  timingBucket?: TimingBucket;
  scheduledTiming?: ActionScheduledTiming;
  /** Stable semantic identity used to reconcile one pending scheduled action across rule branches. */
  scheduleIdentity?: string;
  dedupeWindow?: string;

  /** @deprecated Use notificationRecipientPolicies. */
  recipientPolicies?: RecipientPolicy[];
  /** @deprecated Use notificationRecipientPolicies. */
  recipientPolicy?: RecipientPolicy;
  /** @deprecated */
  suppressIfNoValidRecipient?: boolean;
  /** @deprecated */
  respectCommunicationPreferences?: boolean;
  /** @deprecated */
  templateId?: string;
}

export interface LadderRung {
  id: string;
  name: string;
  description?: string;
  /**
   * @deprecated Branches are always enabled in V2. Ignored during evaluation.
   * Old rules with enabled=false are excluded on migration.
   */
  enabled: boolean;
  /**
   * @deprecated Always-run is removed in V2. Old rules with alwaysRun=true
   * are migrated to unconditional branches (empty condition groups).
   */
  alwaysRun?: boolean;
  conditionGroups: ConditionGroup[];
  conditionGroupOperator?: 'AND' | 'OR';
  actions: RuleAction[];
  priority: number;
  /** True when this rung is the workflow Else lane (runs only if no prior branch matched). */
  isElseBranch?: boolean;
}

export interface TriggerConfig {
  resourceTypes: TriggerResourceType[];
  /** V2 generic operations. Preferred for rules that match multiple lifecycle events. */
  operations?: TriggerOperation[];
  /** V2 generic operation. Retained for backward compatibility with existing rules. */
  operation?: TriggerOperation;
  /** V2 resource field filters. */
  filters?: ResourceFieldFilter[];
  /** @deprecated V1 lifecycle event kinds — converted to operation+filters on read. */
  eventKinds: TriggerEventKind[];
  mode: TriggerMode;
  manualRunEligible: boolean;
  sideEffectsAllowed: boolean;
  /** @deprecated V1 object filters — converted to filters on read. */
  objectFilters: TriggerObjectFilter[];
}

export interface ScopeConfig {
  practiceIds: string[];
  effectiveDateStart?: string;
  effectiveDateEnd?: string;
}

export interface RulePackDraft {
  id?: string;
  stableRuleKey?: string;
  title: string;
  description: string;
  version?: string;
  status: RulePackStatus;
  domain: RulePackDomain;
  trigger: TriggerConfig;
  scope: ScopeConfig;
  conflictPolicy: ConflictPolicy;
  rungs: LadderRung[];
  steps?: WorkflowStep[];
  /** Schema version: 1 = legacy lifecycle, 2 = generic resource trigger. */
  schemaVersion?: RulesEngineSchemaVersion;
}

// ---------------------------------------------------------------------------
// Editor-level workflow step model (visual builder tree)
// ---------------------------------------------------------------------------

export type WorkflowStep =
  | { id: string; kind: 'action'; action: RuleAction }
  | { id: string; kind: 'branch'; branches: WorkflowBranch[]; elseSteps: WorkflowStep[] };

export interface WorkflowBranch {
  id: string;
  name: string;
  description?: string;
  /**
   * @deprecated Always true in V2. Ignored during evaluation.
   */
  enabled: boolean;
  /**
   * @deprecated Removed in V2. Unconditional branches use empty condition groups.
   */
  alwaysRun?: boolean;
  conditionGroups: ConditionGroup[];
  conditionGroupOperator?: 'AND' | 'OR';
  steps: WorkflowStep[];
  priority: number;
}

export interface RulePackListItem {
  id: string;
  title: string;
  description: string;
  version: string;
  status: RulePackStatus;
  domain: RulePackDomain;
  rungCount: number;
  lastUpdated: string;
  triggerMode: TriggerMode;
  triggerResources: TriggerResourceType[];
  triggerOperation?: TriggerOperation;
}

export interface RulesEngineData {
  packs: PlanDefinition[];
}

// ---------------------------------------------------------------------------
// Validation types
// ---------------------------------------------------------------------------

export interface ValidationError {
  path: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

// ---------------------------------------------------------------------------
// Suppression reasons for executor
// ---------------------------------------------------------------------------

export type SuppressionReason =
  | 'no-valid-recipient'
  | 'communication-preferences-disabled'
  | 'duplicate-evaluation-key'
  | 'missing-template'
  | 'missing-form-template'
  | 'missing-required-variable'
  | 'outside-supported-scope'
  | 'dry-run'
  | 'unsupported-action'
  | 'invalid-rule'
  | 'trigger-mismatch'
  | 'notification-creation-failed'
  | 'task-creation-failed'
  | 'email-task-creation-failed'
  | 'form-assignment-failed'
  | 'dedupe-check-failed'
  | 'unsupported-assignment-recipient'
  | 'trigger-filter-mismatch';

// ---------------------------------------------------------------------------
// Trigger filter values (runtime) — kept for V1 compat + V2 runtime
// ---------------------------------------------------------------------------

export interface TriggerFilterValues {
  questionnaireCanonical?: string;
  questionnaireId?: string;
  serviceRequestCodings?: Array<{ system?: string; code?: string }>;
  serviceRequestCategories?: Array<{ system?: string; code?: string }>;
  episodeTypes?: Array<{ system?: string; code?: string; display?: string }>;
  taskCodings?: Array<{ system?: string; code?: string }>;
  /** V2: generic field values extracted from the target resource. */
  resourceFieldValues?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Evaluation context — V1 compat + V2 generic fields
// ---------------------------------------------------------------------------

export interface V1EvaluationContext {
  patient?: {
    active?: boolean;
    email?: { exists?: boolean };
    communicationPreference?: { emailAllowed?: boolean };
    age?: number;
    address?: { state?: string };
  };
  intake?: {
    status?: string;
    submittedAt?: string;
    completedAt?: string;
  };
  episode?: {
    status?: string;
    startedAt?: string;
    type?: string;
    paymentType?: string;
  };
  serviceRequest?: {
    status?: string;
  };
  task?: {
    status?: string;
    code?: string;
  };
  communication?: {
    status?: string;
    medium?: string;
    category?: string;
  };
  activityDefinition?: {
    status?: string;
    name?: string;
  };
  questionnaire?: {
    status?: string;
    url?: string;
  };
  appointment?: {
    status?: string;
    start?: string;
    end?: string;
    minutesDuration?: number;
    category?: string;
    modality?: string;
    serviceType?: string;
    hasPatient?: boolean;
    hasVideoLink?: boolean;
    recurringPattern?: string;
    patientId?: string;
    practitionerId?: string;
    locationId?: string;
    /** Location's stable LOCATION_IDENTIFIER_SYSTEM identifier value (resolved from the Location resource). */
    locationIdentifier?: string;
    /** Location.name (resolved from the Location resource). */
    locationName?: string;
    /** Display address override, else formatted address line (resolved from the Location resource). */
    locationAddress?: string;
    /** Client-facing parking instructions (Location extension; resolved from the Location resource). */
    locationParkingInstructions?: string;
    /** Client-facing arrival instructions (Location extension; resolved from the Location resource). */
    locationArrivalInstructions?: string;
    /** Client-facing waiting room instructions (Location extension; resolved from the Location resource). */
    locationWaitingRoomInstructions?: string;
    description?: string;
  };
  encounter?: {
    status?: string;
    periodStart?: string;
    periodEnd?: string;
    class?: string;
    locationId?: string;
  };
  claim?: {
    type?: string;
    use?: string;
    status?: string;
    provider?: string;
  };
  chargeItem?: {
    status?: string;
    code?: string;
    quantity?: number;
    priceOverride?: number;
  };
  location?: {
    id?: string;
    name?: string;
    address?: { state?: string };
  };
  coverage?: {
    order?: number;
    status?: string;
    payorId?: string;
    relationship?: string;
    type?: string;
  };
  organization?: {
    npiExists?: boolean;
    type?: string;
  };
  practitioner?: {
    npiExists?: boolean;
    npi?: string;
    supervisorNpi?: string;
    taxonomy?: string;
  };
  derived?: {
    hasValidRecipientEmail?: boolean;
    hoursSinceIntakeCompleted?: number;
    hoursSinceEpisodeStarted?: number;
    wasPreviouslySent?: boolean;
    wasPreviouslyAssigned?: boolean;
    /** In-person (ROUTINE) modality with a resolvable Location participant. */
    hasInPersonLocation?: boolean;
    /**
     * Questionnaire ids of the appointment's patient's form-assignment Tasks
     * that are not yet completed (populated for Appointment-triggered
     * evaluation only). Used with the `any-of-incomplete` operator to gate
     * rules on a configurable list of required forms.
     */
    incompleteFormTemplateIds?: string[];
  };
}

// ---------------------------------------------------------------------------
// V1→V2 migration helpers
// ---------------------------------------------------------------------------

/**
 * Convert a V1 TriggerObjectFilter to a V2 ResourceFieldFilter.
 */
export function migrateV1ObjectFilterToFieldFilter(filter: TriggerObjectFilter): ResourceFieldFilter {
  switch (filter.kind) {
    case 'questionnaire-canonical':
      return { id: filter.id, fieldPath: 'questionnaire', operator: 'equals', value: filter.value ?? filter.code ?? '', label: filter.label };
    case 'questionnaire-id':
      return { id: filter.id, fieldPath: 'questionnaire', operator: 'equals', value: filter.value ?? filter.code ?? '', label: filter.label };
    case 'service-request-code':
      return { id: filter.id, fieldPath: 'code.coding', operator: 'equals', value: filter.code ?? '', system: filter.system, label: filter.label };
    case 'service-request-category':
      return { id: filter.id, fieldPath: 'category.coding', operator: 'equals', value: filter.code ?? '', system: filter.system, label: filter.label };
    case 'episode-type':
      return { id: filter.id, fieldPath: 'episode.type', operator: 'equals', value: filter.code ?? '', system: filter.system, label: filter.label };
    case 'task-code':
      return { id: filter.id, fieldPath: 'task.code', operator: 'equals', value: filter.code ?? '', system: filter.system, label: filter.label };
    default:
      return { id: filter.id, fieldPath: 'unknown', operator: 'equals', value: filter.value ?? '', label: filter.label };
  }
}

/**
 * Convert V1 lifecycle trigger config to V2 generic trigger config.
 * Preserves backward compatibility: old eventKinds/objectFilters remain
 * for runtime compat, but new operation/filters are populated.
 */
export function migrateV1TriggerToV2(trigger: TriggerConfig): TriggerConfig {
  const eventKind = trigger.eventKinds[0];
  const mapping = eventKind ? V1_EVENT_TO_GENERIC[eventKind] : undefined;

  const operation: TriggerOperation = mapping?.operation ?? 'created';
  const filters: ResourceFieldFilter[] = (trigger.objectFilters ?? []).map(migrateV1ObjectFilterToFieldFilter);

  return {
    ...trigger,
    operations: [operation],
    operation,
    filters,
  };
}
